import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  useTheme,
  alpha,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle as AcceptIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  Business as BusinessIcon,
  Message as MessageIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { ApplicationService } from '../../services/applicationService';
import toast from 'react-hot-toast';

interface Application {
  id: string;
  status: string;
  ai_application?: boolean; // Indicates if this application was generated by AI
  product?: {
    id: string;
    name: string;
  };
  applicant?: {
    id: string;
    first_name?: string;
    last_name?: string;
    alias?: string;
  };
}

interface ApplicationStatusActionsProps {
  application: Application;
  onStatusUpdate?: (applicationId: string, newStatus: string) => void;
  onViewApplication?: () => void;
  onContact?: () => void;
  onViewPosting?: () => void;
}

const ApplicationStatusActions: React.FC<ApplicationStatusActionsProps> = ({
  application,
  onStatusUpdate,
  onViewApplication,
  onContact,
  onViewPosting,
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    action: 'accepted' | 'rejected' | null;
  }>({ open: false, action: null });

  const handleStatusUpdate = async (status: 'accepted' | 'rejected') => {
    setLoading(true);
    try {
      await ApplicationService.updateApplicationStatus(application.id, status);
      onStatusUpdate?.(application.id, status);
      
      const actionText = status === 'accepted' ? 'accepted' : 'rejected';
      toast.success(`Application ${actionText} successfully! 🎉`);
      
      setConfirmDialog({ open: false, action: null });
    } catch (error) {
      console.error('Error updating application status:', error);
      toast.error('Failed to update application status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusChip = (status: string) => {
    const statusConfig = {
      submitted: { label: 'SUBMITTED', color: 'default' as const },
      review: { label: 'UNDER REVIEW', color: 'warning' as const },
      accepted: { label: 'ACCEPTED', color: 'success' as const },
      rejected: { label: 'REJECTED', color: 'error' as const },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.submitted;
    
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        sx={{
          fontWeight: 'bold',
          textTransform: 'uppercase',
          fontSize: '0.75rem',
          height: 24,
        }}
      />
    );
  };

  const canUpdateStatus = application.status === 'submitted' || application.status === 'review';

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 200 }}>
        {/* Status Chip */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1, gap: 1, flexWrap: 'wrap' }}>
          {getStatusChip(application.status)}
          {application.ai_application && (
            <Chip 
              label="AI Generated" 
              size="small" 
              color="primary" 
              variant="outlined"
              sx={{ 
                fontWeight: 'bold',
                borderColor: 'primary.main',
                color: 'primary.main',
                '& .MuiChip-label': {
                  fontSize: '0.75rem'
                }
              }}
            />
          )}
        </Box>

        {/* Primary Action Buttons */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<ViewIcon />}
            onClick={onViewApplication}
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
              },
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 'bold',
              py: 1,
            }}
          >
            View Application
          </Button>

          {onViewPosting && (
            <Button
              variant="outlined"
              startIcon={<BusinessIcon />}
              onClick={onViewPosting}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 'bold',
                py: 1,
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': {
                  borderColor: 'primary.dark',
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                },
              }}
            >
              Open Posting
            </Button>
          )}

          {onContact && (
            <Button
              variant="text"
              startIcon={<MessageIcon />}
              onClick={onContact}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 'bold',
                py: 1,
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.text.secondary, 0.1),
                },
              }}
            >
              Contact
            </Button>
          )}
        </Box>

        {/* Accept/Reject Actions */}
        {canUpdateStatus && (
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button
              variant="contained"
              startIcon={<AcceptIcon />}
              onClick={() => setConfirmDialog({ open: true, action: 'accepted' })}
              disabled={loading}
              sx={{
                flex: 1,
                background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.success.dark} 0%, ${theme.palette.success.main} 100%)`,
                },
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 'bold',
                py: 1,
              }}
            >
              Accept
            </Button>
            <Button
              variant="contained"
              startIcon={<RejectIcon />}
              onClick={() => setConfirmDialog({ open: true, action: 'rejected' })}
              disabled={loading}
              sx={{
                flex: 1,
                background: `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.error.dark} 0%, ${theme.palette.error.main} 100%)`,
                },
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 'bold',
                py: 1,
              }}
            >
              Reject
            </Button>
          </Box>
        )}

        {/* Loading Indicator */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
            <CircularProgress size={20} />
          </Box>
        )}
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, action: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            {confirmDialog.action === 'accepted' ? (
              <AcceptIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
            ) : (
              <RejectIcon sx={{ fontSize: 48, color: 'error.main', mb: 1 }} />
            )}
          </motion.div>
          <Typography variant="h6" fontWeight="bold">
            {confirmDialog.action === 'accepted' ? 'Accept Application' : 'Reject Application'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Are you sure you want to {confirmDialog.action === 'accepted' ? 'accept' : 'reject'} this application?
          </Typography>
          {application.applicant && (
            <Typography variant="body2" color="text.secondary">
              Applicant: {application.applicant.first_name || ''} {application.applicant.last_name || ''}
              {application.applicant.alias && ` (@${application.applicant.alias})`}
            </Typography>
          )}
          {application.product && (
            <Typography variant="body2" color="text.secondary">
              Position: {application.product.name}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            onClick={() => setConfirmDialog({ open: false, action: null })}
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => confirmDialog.action && handleStatusUpdate(confirmDialog.action)}
            variant="contained"
            disabled={loading}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 'bold',
              background: confirmDialog.action === 'accepted' 
                ? `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`
                : `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`,
              '&:hover': {
                background: confirmDialog.action === 'accepted'
                  ? `linear-gradient(135deg, ${theme.palette.success.dark} 0%, ${theme.palette.success.main} 100%)`
                  : `linear-gradient(135deg, ${theme.palette.error.dark} 0%, ${theme.palette.error.main} 100%)`,
              },
            }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              confirmDialog.action === 'accepted' ? 'Accept' : 'Reject'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ApplicationStatusActions;
